<template>
    <div class="detail" :class="[$mq]">
        <div class="top-container" :class="{ infoOpened: infoIsOpen }">
            <AppccTopDetail @backButton="back" :hasMoreOptions="false" :title="template.name" :hasReopen="false" :hasDelete="false" />
            <div class="top-actions-container">
                <input v-if="!hasError" type="text" class="search" v-model="search" :placeholder="$t('vtf.datatable.search')" />

                <!-- EDIT COLUMNS BUTTON -->
                <button v-if="activeTab === 'audit'" @click="editAuditTableColumns" class="edit-columns"></button>

                <!-- ADD AUDIT BUTTON -->
                <button v-if="activeTab === 'audit' && Object.values(this.audits).length > 0 && this.userAppAccess && checkPermissions()" @click="emitAddEdit('add')" class="add-report"></button>
            </div>
        </div>

        <!-- TABS (AUDIT/ACTION PLAN) -->
        <div v-if="locationHasActionPlansTool" class="tabs-container">
            <div class="tabs">
                <button class="tab" :class="{ active: activeTab === 'audit' }" @click="switchTab('audit')">
                    <span class="tab-text">
                        {{ $t('page_title.Audit') }}
                        <span v-if="auditsOpenCount > 0" class="open-count">({{ auditsOpenCount }})</span>
                    </span>
                </button>
                <button class="tab" :class="{ active: activeTab === 'action_plan' }" @click="switchTab('action_plan')">
                    <span class="tab-text">
                        {{ $t('audit.headers.action_plan') }}
                        <span v-if="actionPlansOpenCount > 0" class="open-count">({{ actionPlansOpenCount }})</span>
                    </span>
                </button>
            </div>
        </div>

        <input v-if="!hasError" type="text" class="search mobile-search" v-model="search" :placeholder="$t('vtf.datatable.search')" />

        <!-- AUDITS TABLE -->
        <template v-if="activeTab === 'audit'">
            <div v-if="(searchedValues.length > 0 || search) && !hasError" ref="datatable" class="table-container" :class="{ hasTabs: locationHasActionPlansTool }">
                <BaseTable 
                    :headers="baseTableHeaders" 
                    :rows="searchedValues"
                    :totalItems="getTotalItems"
                    :pageNumber="getPageNumber"
                    :itemsPerPage="getItemsPerPage"
                    :sortColumn="sortColumn"
                    :sortDirection="sortDirection"
                    @row-click="handleRowClick"
                    @sort-change="handleSortChange"
                    @back-button-click="goToPreviousPage"
                    @forward-button-click="goToNextPage"
                >
                    <!-- Slot para Status -->
                    <template #cell-status="{ value, row }">
                        <ColorAndTextTag 
                            v-if="row.statusData" 
                            :circleColor="row.statusData.color" 
                            :text="row.statusData.name.toUpperCase()" 
                        />
                        <span v-else>-</span>
                    </template>
                    
                    <!-- Slot para Employee -->
                    <template #cell-employee="{ value, row }">
                        <AuditEmployeeCell :employeeData="row.employeeData" />
                    </template>
                    
                    <!-- Slot para Action Plans -->
                    <template #cell-actionPlans="{ value, row }">
                        <AuditActionPlansCell :actionPlansData="row.actionPlansData" />
                    </template>
                    
                    <!-- Slot para Score (landscape/desktop mode) -->
                    <template #cell-score="{ value, row }">
                        <div class="final-score">
                            <span v-if="row.finalScoreColor" 
                                  class="final-score-color" 
                                  :style="{ backgroundColor: row.finalScoreColor }">
                            </span>
                            <span class="name-and-score">{{ value }}</span>
                        </div>
                    </template>
                    
                    <!-- Slot para Date (landscape/desktop mode) -->
                    <template #cell-date="{ value, row }">
                        <span :class="{ 'require-sync-icon': row.requireSync }">{{ value }}</span>
                    </template>
                    
                    <!-- Slot para SimpleDate (portrait mode) -->
                    <template #cell-simpleDate="{ value, row }">
                        <span :class="{ 'require-sync-icon': row.requireSync }">{{ value }}</span>
                    </template>
                    
                    <!-- Slot para Auditory (portrait mode - combina status y score) -->
                    <template #cell-auditory="{ value, row }">
                        <div class="audit-info">
                            <ColorAndTextTag 
                                v-if="row.auditoryData" 
                                :circleColor="row.auditoryData.color" 
                                :text="row.auditoryData.name.toUpperCase()" 
                            />
                            <div v-if="row.auditoryScore && row.auditoryScore !== '-'" class="final-score">
                                <span v-if="row.finalScoreColor" 
                                      class="final-score-color" 
                                      :style="{ backgroundColor: row.finalScoreColor }">
                                </span>
                                <span class="name-and-score">{{ row.auditoryScore }}</span>
                            </div>
                            <span v-else-if="!row.auditoryScore || row.auditoryScore === '-'">-</span>
                        </div>
                    </template>
                </BaseTable>
            </div>
            <ConnectionBox gType="registerConnection" @reload="reloadAudit" v-else-if="enabledConnexionBox()"></ConnectionBox>
            <EmptyPage v-else-if="getAuditHasApiError" :type="'error'" :title="$t('empty_pages.api_error_title')" :description="$t('empty_pages.api_error_description')" />
            <EmptyPage v-else-if="searchedValues.length == 0 && !hasError && !search" @buttonAction="emitAddEdit" :title="$t('empty_pages.audit_title')" type="record" :description="$t('empty_pages.audit_description')" :buttonText="$t('empty_pages.start')" />
            <EmptyPage v-if="hasError" :buttonText="$t('empty_pages.return_audits')" @buttonAction="back()" buttonClass="back" :type="'error'" :title="$t('assets.overlay.timeout.message')" :description="$t('empty_pages.error_audit')" />
        </template>

        <!-- ACTION PLANS TABLE -->
        <template v-else>
            <ActionPlansTable :search="search" />
        </template>
    </div>
</template>

<script>
import BaseTable from '@/components/domain/ui/BaseTable.vue'
import ColorAndTextTag from '@/components/domain/ui/ColorAndTextTag.vue'
import AuditEmployeeCell from './AuditEmployeeCell.vue'
import AuditActionPlansCell from './AuditActionPlansCell.vue'
import AppccTopDetail from '../../layout/AppccTopDetail.vue'
import EmptyPage from '../../layout/EmptyPage.vue'
import { FORMAT } from '@/constants'
import ConnectionBox from '@/components/domain/ui/Box.vue'
import ActionPlansTable from '@/components/domain/actionPlans/ActionPlansTable.vue'
import { formatDisplayDateFromStore } from '@/utils/dateFormatters'

export default {
    components: {
        BaseTable,
        ColorAndTextTag,
        AuditEmployeeCell,
        AuditActionPlansCell,
        EmptyPage,
        AppccTopDetail,
        ConnectionBox,
        ActionPlansTable
    },
    props: {
        audits: { type: Object },
        detailId: { type: String },
        hasError: { type: undefined, default: false }
    },
    data() {
        return {
            infoIsOpen: false,
            search: '',
            pagination: {
                sortBy: 'dateUnix',
                rowsPerPage: -1,
                descending: true
            },
            hidden_columns: localStorage.col_audit ? JSON.parse(localStorage.col_audit) : [],
            FORMAT: FORMAT,
            page: 1,
            num_items: 20,
            columnSortedDesc: false,
            columnSortedAsc: false,
            headersConf: localStorage.getItem(`headersConf_${this.detailId}`) ? JSON.parse(localStorage.getItem(`headersConf_${this.detailId}`)) : [],
            isReloaded: false,
            activeTab: 'audit'
        }
    },
    computed: {
        locationHasActionPlansTool() {
            return this.$store.getters['login/getLocationHasActionPlansTool']
        },
        auditsOpenCount() {
            return this.$store.getters['audit/getOpenCount'] || 0
        },
        actionPlansOpenCount() {
            return this.$store.getters['actionplan/getOpenCount'] || 0
        },
        getAuditHasApiError() {
            return this.$store.getters['audit/getHasApiError']
        },
        checkConnetion() {
            return this.$store.getters['getConnState']
        },
        userAppAccess() {
            return this.$store.getters['loginUser/getHasAccessApp']
        },
        results() {
            return this.$store.getters['audit/getNumberItems'] ? this.$store.getters['audit/getNumberItems'] : false
        },
        getPageNumber() {
            return this.page || 1
        },
        getItemsPerPage() {
            return this.num_items || 20
        },
        getTotalItems() {
            return this.results || 0
        },
        template() {
            // ESCONDER BOTTONES
            if (this.hasError) {
                this.$bar.hide()
            }
            return this.$store.getters['audit/getTemplateById'](this.detailId)
        },
        user_id() {
            return this.$store.getters['loginUser/getLocalEmployee']
        },
        user() {
            var employee = this.$store.getters['employee/getEmployee'](this.user_id)
            if (employee) {
                return employee
            }
        },
        headers: {
            get: function () {
                const config = self.$store.getters['login/getConfig']
                const tools = config.tools

                let schema = this.$store.getters['audit/getHeaders']
                var headers = []

                if (this.$mq == 'portrait') {
                    headers.push({
                        text: 'id',
                        value: 'issue_id',
                        sortable: true,
                        visible: true,
                        blocked: true
                    })
                    headers.push({
                        text: this.$t('audit.headers.date'),
                        value: 'simpleDate',
                        sortable: true,
                        visible: true,
                        blocked: true
                    })
                    headers.push({
                        text: this.$t('audit.headers.status'),
                        value: 'auditory',
                        sortable: false,
                        visible: true,
                        blocked: true
                    })
                } else {
                    headers.push({
                        text: 'id',
                        value: 'issue_id',
                        sortable: true,
                        visible: true,
                        blocked: true
                    })
                    headers.push({
                        text: this.$t('audit.headers.date'),
                        value: 'date',
                        sortable: true,
                        visible: true,
                        blocked: true
                    })
                    headers.push({
                        text: this.$t('audit.headers.status'),
                        value: 'status',
                        type: 'status',
                        sortable: true,
                        visible: true,
                        blocked: true
                    })
                    headers.push({
                        text: this.$t('audit.headers.supervised'),
                        value: 'employee',
                        type: 'employee',
                        sortable: true,
                        visible: true,
                        blocked: true
                    })
                    headers.push({
                        text: this.$t('audit.headers.score'),
                        value: 'score',
                        type: 'score',
                        sortable: true,
                        visible: true,
                        blocked: true
                    })
                    headers.push({
                        text: this.$t('audit.headers.action_plan'),
                        value: 'actionPlans',
                        type: 'actionPlans',
                        sortable: true,
                        visible: tools.includes(19),
                        blocked: false
                    })
                }

                const schemaValues = Object.values(schema || {})
                if (schemaValues.length > 0) {
                    const titlesSchema = schemaValues.filter((header) => header && header.type !== undefined && header.type !== null && header.type !== 8 && header.type !== 9).map((value) => value)
                    titlesSchema.forEach((header) => {
                        if (header && header.title && header.id !== undefined) {
                            headers.push({
                                text: header.title,
                                value: header.id,
                                sortable: true,
                                type: header.type || null,
                                visible: true,
                                blocked: false
                            })
                        }
                    })
                }

                if (this.headersConf.length > 0) {
                    if (this.headersConf.length !== headers.length) {
                        //Ver campos que tiene headerConf y modificarlos en headers
                        this.headersConf.forEach((headerConf) => {
                            const index = headers.findIndex((header) => header.value === headerConf.value)
                            if (index !== -1) {
                                headers[index].visible = headerConf.visible
                            }
                        })
                        return JSON.parse(JSON.stringify(headers))
                    }
                    return JSON.parse(JSON.stringify(this.headersConf))
                }

                return headers
            },
            set: function (value) {
                this.headers = value
            }
        },
        searchedValues() {
            var self = this
            // Parsear y normalizar IDs de auditoría del campo de búsqueda una sola vez antes del loop
            const auditIds = self.search ? self.parseAuditIds(self.search) : []
            const resultFiltered = Object.values(self.audits).reduce((acc, obj) => {
                const templateId = obj.template_id

                if (self.search) {
                    const searchLower = self.search.toLowerCase()
                    let status = ''
                    let auditScore = ''

                    if (obj.status == 'progress') {
                        status = this.$t('audit.status.progress')
                    } else if (obj.status == 'completed') {
                        status = this.$t('audit.status.completed')
                    } else {
                        status = this.$t('audit.status.to_start')
                    }

                    if (obj.finalScore) {
                        auditScore = obj.finalScore.name
                    } else {
                        auditScore = '-'
                    }

                    // Comparar issue_id (puede ser número o string) con los IDs parseados
                    const objIssueId = obj.issue_id ? obj.issue_id.toString().replace(/^0+/, '') || '0' : null
                    const matchesAuditId = auditIds.length > 0 && objIssueId ? auditIds.includes(objIssueId) : false

                    // Verificar coincidencias con los filtros existentes
                    const matchesStatus = status && status.toLowerCase().includes(searchLower)
                    // Buscar en employee_ref o employees para mantener compatibilidad
                    const employeeName = (obj.employee_ref && obj.employee_ref.name) 
                        ? obj.employee_ref.name 
                        : (Object.keys(obj.employees || {}).length > 0 
                            ? obj.employees[Object.keys(obj.employees)[0]].name 
                            : null)
                    const matchesEmployee = employeeName && employeeName.toLowerCase().includes(searchLower)
                    const matchesScore = auditScore && auditScore.toLowerCase().includes(searchLower)

                    // Si hay IDs específicos en la búsqueda, priorizar la coincidencia exacta con IDs
                    // Si no hay IDs, usar los filtros existentes (Estado, Empleado, Score)
                    const shouldInclude = templateId !== null && templateId !== undefined && obj.type !== 14 && obj.type !== 5 && (
                        (auditIds.length > 0 && matchesAuditId) ||
                        (auditIds.length === 0 && (matchesStatus || matchesEmployee || matchesScore))
                    )

                    if (shouldInclude) {
                        acc[obj.id] = obj
                    }
                } else if (templateId === self.$route.params.id) {
                    acc[obj.id] = obj
                }

                return acc
            }, {})

            const hashIDs = resultFiltered ? Object.keys(resultFiltered) : false

            // Función pura: solo retorna el resultado sin efectos secundarios
            return self.fillTable(self.audits, hashIDs)
        },
        baseTableHeaders() {
            // Validar que headers estén cargados antes de procesar
            // headers es un computed que retorna un array
            const headersArray = this.headers
            if (!headersArray || !Array.isArray(headersArray) || headersArray.length === 0) {
                return []
            }
            return headersArray
                .filter(h => h && h.visible)
                .map(h => ({
                    text: h.text ? h.text.toUpperCase() : '',
                    key: h.value, // Mapear 'value' a 'key' para BaseTable
                    sortable: h.sortable,
                    visible: h.visible,
                    blocked: h.blocked,
                    type: (h && h.type !== undefined && h.type !== null) ? h.type : null // Incluir tipo para identificar PHOTO/SIGNATURE
                }))
        },
        sortColumn() {
            return this.columnSortedDesc || this.columnSortedAsc || null
        },
        sortDirection() {
            if (this.columnSortedDesc && !this.columnSortedAsc) return 'desc'
            if (this.columnSortedAsc) return 'asc'
            return null
        }
    },
    methods: {
        switchTab(tab) {
            if (this.activeTab === tab) {
                // do nothing if the tab is already active
                return
            }

            this.activeTab = tab
            this.search = ''
        },
        reloadAudit() {
            if (fnCheckConnection()) {
                this.$store.dispatch('audit/loadItems', { template: this.$route.params.id })

                this.isReloaded = true
            }
        },
        enabledConnexionBox() {
            if (this.searchedValues.length == 0 && !fnCheckConnection() && !this.isReloaded) {
                return true
            }
        },
        checkPermissions() {
            const template = this.template
            const userRole = this.$store.getters['employee/getRol'](this.user.rol)
            if (userRole.level >= 8) {
                return true
            }

            if (template && template.config && template.config.nRoles) {
                const nRoles = template.config.nRoles
                let isValid = false

                nRoles.forEach((role) => {
                    if (role.roleId == userRole.id && role.permissions.includes('w')) {
                        isValid = true
                        return
                    }
                })
                return isValid
            }

            return true
        },
        editAuditTableColumns() {
            if (this.activeTab !== 'audit') {
                return
            }
            const self = this
            const headersCopy = JSON.parse(JSON.stringify(this.headers))
            this.$popup.tableColumn({
                title: this.$t('supervise.custom_columns_popup.title'),
                textSave: this.$t('button.save'),
                headers: this.headers,
                routePop: self.$route.name,
                callSave: (obj) => {
                    self.headersConf = obj.$data.headers
                    localStorage.setItem(`headersConf_${self.detailId}`, JSON.stringify(obj.$data.headers))
                    self.$popup.close()
                    self.$forceUpdate()
                },
                headers: headersCopy,
                callCancel: function () {
                    this.$popup.close()
                }
            })
        },
        // commented this method because is never called
        // prepareTable(audit) {
        //     var values = []
        //     for (var i in audit) {
        //         var raw = audit[i]
        //         var row = {}
        //         // row.edit = "";
        //         // row.select = "";
        //         // row.dateUnix = raw.created_date ? moment(raw.created_date).unix() : '-'
        //         row.date = raw.created_date ? moment(raw.created_date).format((this.$store.getters['login/getConfig'].dateFormat || this.$store.getters['login/getConfig'].dateFormat || 'DD/MM/YYYY ') + '  HH:mm') : '-'
        //         row.statusB = raw.status === 'completed' ? 0 : 1
        //         row.employee = raw.employee && raw.employee != null ? raw.employee.name : '-'
        //         row.id = raw.id ? raw.id : ''

        //         for (var j in raw.items) {
        //             var item = raw.items[j]
        //             row.type = item.type

        //             row[item.item_id] = item.value
        //         }

        //         values.push(row)
        //     }
        //     return values
        // },
        fillTable(audits, index = false) {
            let values = []
            const self = this
            const isPortrait = self.$mq === 'portrait'

            for (const i in audits) {
                // Early return: saltar si el índice no está incluido
                if (index && !index.includes(i)) {
                    continue
                }

                const raw = audits[i]
                const row = this.buildTableRow(raw, i, isPortrait)
                values.push(row)
            }

            return this.sortTableValues(values)
        },
        buildTableRow(raw, index, isPortrait) {
            const row = {
                id: raw.id || index,
                issue_id: raw.issue_id ? `#${this.addZeros(raw.issue_id, 2)}` : '-',
                dateUnix: raw.created_date ? moment(raw.created_date).unix() : '-',
                requireSync: raw.requireSync ? raw.requireSync : false,
                statusB: raw.status === 'completed' ? 0 : 1
            }

            // Campos comunes a ambos modos
            const statusData = raw.status ? this.getStatusData(raw.status) : null
            const actionPlansData = this.getActionPlansData(raw)

            if (isPortrait) {
                // Portrait mode: columnas simplificadas
                row.simpleDate = this.formatDate(raw, true)
                row.auditoryData = statusData
                row.auditoryScore = this.formatScore(raw, false) // Sin completed/total_score
                row.finalScoreColor = raw.finalScore && raw.finalScore.color ? raw.finalScore.color : null
                row.actionPlansData = actionPlansData
            } else {
                // Landscape/Desktop mode: columnas completas
                row.date = this.formatDate(raw, false)
                row.statusData = statusData
                row.employeeData = this.getEmployeeData(raw)
                row.score = this.formatScore(raw, true) // Con completed/total_score
                row.finalScoreColor = raw.finalScore && raw.finalScore.color ? raw.finalScore.color : null
                row.actionPlansData = actionPlansData
            }

            // Campos adicionales del schema
            const schemaFields = this.getSchemaFields(raw)
            Object.assign(row, schemaFields)

            return row
        },
        getSortableValue(row, columnKey) {
            // Mapeo de claves del header a claves reales de los datos
            const keyMapping = {
                'status': 'statusData',
                'employee': 'employeeData',
                'actionPlans': 'actionPlansData',
                'date': 'dateUnix',
                'simpleDate': 'dateUnix'
            }

            const actualKey = keyMapping[columnKey] || columnKey
            const value = row[actualKey]

            // Si el valor es un objeto, extraer un valor ordenable
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                // Para statusData y employeeData, ordenar por name
                if (value.name) {
                    return value.name
                }
                // Para actionPlansData, ordenar por percentage
                if (value.percentage !== undefined) {
                    return value.percentage
                }
            }

            return value
        },
        sortTableValues(values) {
            const self = this

            // Early return: no hay ordenamiento
            if (!self.columnSortedDesc && !self.columnSortedAsc) {
                return values
            }

            // Ordenamiento descendente
            if (self.columnSortedDesc && !self.columnSortedAsc) {
                return values.sort(function (a, b) {
                    const aVal = self.getSortableValue(a, self.columnSortedDesc)
                    const bVal = self.getSortableValue(b, self.columnSortedDesc)

                    // Manejar null/undefined: tratarlos como valores especiales
                    const aIsNull = aVal === null || aVal === undefined
                    const bIsNull = bVal === null || bVal === undefined

                    // Si ambos son null/undefined, son iguales
                    if (aIsNull && bIsNull) {
                        return 0
                    }

                    // Si solo uno es null/undefined, null va al final (menor valor)
                    if (aIsNull) {
                        return 1
                    }
                    if (bIsNull) {
                        return -1
                    }

                    // Verificar si ambos son strings válidos
                    const aIsString = typeof aVal === 'string'
                    const bIsString = typeof bVal === 'string'

                    // Si ambos son strings, usar localeCompare
                    if (aIsString && bIsString) {
                        return bVal.localeCompare(aVal)
                    }

                    // Si uno es string y el otro no, convertir a número para comparación
                    // Los strings se convierten a NaN, que se manejará después
                    const aNum = aIsString ? NaN : Number(aVal)
                    const bNum = bIsString ? NaN : Number(bVal)

                    // Si ambos son números válidos, comparar numéricamente
                    if (!isNaN(aNum) && !isNaN(bNum)) {
                        return bNum - aNum
                    }

                    // Si uno es NaN (string) y el otro es número, el string va después
                    if (isNaN(aNum) && !isNaN(bNum)) {
                        return 1
                    }
                    if (!isNaN(aNum) && isNaN(bNum)) {
                        return -1
                    }

                    // Ambos son NaN (strings que no se pudieron convertir), comparar como strings
                    return String(bVal).localeCompare(String(aVal))
                })
            }

            // Ordenamiento ascendente
            if (self.columnSortedAsc) {
                return values.sort(function (a, b) {
                    const aVal = self.getSortableValue(a, self.columnSortedAsc)
                    const bVal = self.getSortableValue(b, self.columnSortedAsc)

                    // Manejar null/undefined: tratarlos como valores especiales
                    const aIsNull = aVal === null || aVal === undefined
                    const bIsNull = bVal === null || bVal === undefined

                    // Si ambos son null/undefined, son iguales
                    if (aIsNull && bIsNull) {
                        return 0
                    }

                    // Si solo uno es null/undefined, null va al principio (menor valor)
                    if (aIsNull) {
                        return -1
                    }
                    if (bIsNull) {
                        return 1
                    }

                    // Verificar si ambos son strings válidos
                    const aIsString = typeof aVal === 'string'
                    const bIsString = typeof bVal === 'string'

                    // Si ambos son strings, usar localeCompare
                    if (aIsString && bIsString) {
                        return aVal.localeCompare(bVal)
                    }

                    // Si uno es string y el otro no, convertir a número para comparación
                    // Los strings se convierten a NaN, que se manejará después
                    const aNum = aIsString ? NaN : Number(aVal)
                    const bNum = bIsString ? NaN : Number(bVal)

                    // Si ambos son números válidos, comparar numéricamente
                    if (!isNaN(aNum) && !isNaN(bNum)) {
                        return aNum - bNum
                    }

                    // Si uno es NaN (string) y el otro es número, el string va después
                    if (isNaN(aNum) && !isNaN(bNum)) {
                        return 1
                    }
                    if (!isNaN(aNum) && isNaN(bNum)) {
                        return -1
                    }

                    // Ambos son NaN (strings que no se pudieron convertir), comparar como strings
                    return String(aVal).localeCompare(String(bVal))
                })
            }

            return values
        },
        getEmployeeData(raw) {
            // Priorizar employee_ref
            if (raw.employee_ref && raw.employee_ref.name) {
                return {
                    name: raw.employee_ref.name,
                    avatar: raw.employee_ref.avatar
                }
            }
            // Si no hay employee_ref, usar employees
            if (Object.keys(raw.employees || {}).length > 0) {
                const firstEmployee = raw.employees[Object.keys(raw.employees)[0]]
                // Validar que firstEmployee existe y tiene la propiedad name
                if (firstEmployee && firstEmployee.name) {
                    return {
                        name: firstEmployee.name,
                        avatar: firstEmployee.avatar || (raw.employee_ref && raw.employee_ref.avatar)
                    }
                }
            }
            return null
        },
        formatScore(raw, includeCompletedTotal = true) {
            if (!raw.complete_date) {
                return '-'
            }

            const score = raw.score !== undefined && raw.score !== null ? raw.score : 0

            if (raw.finalScore && raw.finalScore.name) {
                return `${raw.finalScore.name} (${score}%)`
            }

            // Solo incluir completed/total_score en landscape/desktop
            if (includeCompletedTotal && raw.completed && raw.total_score) {
                return `${raw.completed}/${raw.total_score} (${score}%)`
            }

            return `${score}%`
        },
        formatDate(raw, isPortrait = false) {
            if (!raw.created_date) {
                return '-'
            }

            return formatDisplayDateFromStore(raw.created_date, {
                includeTime: isPortrait,
                emptyValue: '-'
            })
        },
        getActionPlansData(raw) {
                    let actionPlansData = {
                        open: 0,
                        in_progress: 0,
                        completed: 0,
                others: 0,
                percentage: 0
                    }

                    if (raw.action_plans && typeof raw.action_plans === 'object') {
                        actionPlansData = {
                            open: raw.action_plans.open || 0,
                            in_progress: raw.action_plans.in_progress || 0,
                            completed: raw.action_plans.completed || 0,
                            others: raw.action_plans.others || 0,
                            percentage: 0
                        }

                const total = actionPlansData.open + actionPlansData.in_progress + 
                             actionPlansData.completed + actionPlansData.others

                        if (total > 0) {
                    actionPlansData.percentage = Math.round(
                        (actionPlansData.completed / total) * 100
                    )
                }
            } else if (raw.data) {
                actionPlansData.percentage = this.$store.getters['audit/getActionPlanPercentage'](raw.id) || 0
            }
            
            return actionPlansData
        },
        getSchemaFields(raw) {
            const fields = {}
            const schema = this.$store.getters['audit/getHeaders']
            const schemaValues = Object.values(schema || {})
            
            if (schemaValues.length > 0 && raw.items) {
                // raw.items puede ser un objeto o un array
                const itemsArray = Array.isArray(raw.items) 
                    ? raw.items 
                    : Object.values(raw.items || {})
                
                schemaValues.forEach((header) => {
                    if (header && header.type !== undefined && header.type !== null && header.type !== 8 && header.type !== 9) {
                        const item = itemsArray.find(item => item && item.item_id === header.id)
                        if (item && item.value !== undefined && item.value !== null) {
                            fields[header.id] = this.format(item.value, header.type)
                        } else {
                            fields[header.id] = '-'
                        }
                    }
                })
            }
            
            return fields
        },
        back() {
            this.$router.push({ name: 'Audit' })
        },
        sortTable(obj) {
            this.columnSortedDesc = obj.columnSortedDesc
            this.columnSortedAsc = obj.columnSortedAsc
        },
        handleRowClick(row) {
            this.emitAddEdit(row.id)
        },
        handleSortChange({ column, direction }) {
            if (direction === 'desc') {
                this.columnSortedDesc = column
                this.columnSortedAsc = false
            } else {
                this.columnSortedDesc = false
                this.columnSortedAsc = column
            }
        },
        filterColumns() {
            var self = this
            this.$filter({
                list: this.headers,
                selected: self.hidden_columns,
                callback: function (d) {
                    self.hidden_columns = [...d]
                    self.saveFilter(self.hidden_columns)
                }
            })
            this.$filter.show()
        },
        filter() {
            if (fnCheckConnection()) {
                var self = this
                var params = {
                    id: this.$route.params.id,
                    number_pages: this.page,
                    number_items: this.num_items
                }
                this.$overlay.loading()
                this.$store.dispatch('audit/loadItemsPagination', params).then(function () {
                    self.$overlay.hide()
                })
            }
        },
        scrollDatataTable() {
            setTimeout(() => {
                const tableElement = this.$refs.datatable
                if (tableElement) {
                    tableElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
                }
            }, 100)
        },
        goToPreviousPage() {
            if (this.page > 1) {
                this.search = ''
                this.page -= 1
                this.filter()
                this.scrollDatataTable()
            }
        },
        goToNextPage() {
            const totalPages = Math.ceil(this.getTotalItems / this.getItemsPerPage)
            if (this.page < totalPages) {
                this.search = ''
                this.page += 1
                this.filter()
                this.scrollDatataTable()
            }
        },

        // Method to format numbers to order correctly
        addZeros(number, length) {
            var str = '' + number
            while (str.length < length) {
                str = '0' + str
            }
            return str
        },

        saveFilter(filter) {
            if (localStorage) {
                localStorage.col_audit = JSON.stringify(filter)
            }
        },

        changeSort(column) {
            if (this.pagination.sortBy === column) {
                this.pagination.descending = !this.pagination.descending
            } else {
                this.pagination.sortBy = column
                this.pagination.descending = false
            }
        },
        emitAddEdit(id) {
            this.$emit('sendedAddedit', id)
        },
        format(value, type) {
            // Convertir type a string para comparación consistente
            const typeStr = String(type)
            switch (typeStr) {
                case String(FORMAT.CHECK):
                case String(FORMAT.YESNO):
                case String(FORMAT.YESNONC):
                    return value

                case String(FORMAT.DATE):
                    return value ? formatDisplayDateFromStore(value, { includeTime: false, emptyValue: '-' }) : '-'

                case String(FORMAT.DATETIME):
                    return value ? formatDisplayDateFromStore(value, { includeTime: true, emptyValue: '' }) : ''

                case String(FORMAT.PHOTO):
                case String(FORMAT.SIGNATURE):
                    // Retornar '-' ya que no se muestran imágenes en la tabla
                    return '-'

                case String(FORMAT.TITLE):
                case String(FORMAT.SUBTITLE):
                    return value || '-'

                default:
                    return value
            }
        },
        classes(value, current, type) {
            let val = value

            switch (type) {
                case 'score':
                    return {
                        score: val != '-'
                    }

                case String(FORMAT.TITLE):
                case String(FORMAT.SUBTITLE):
                    val = current ? current.replace('%', '') : 0

                    return {
                        success: val == 100,
                        progress: val > 75 && val < 100,
                        warning: val > 0 && val <= 75,
                        error: val == 0
                    }

                case 'status':
                    return {
                        completed: value.status.includes(this.$t('audit.status.completed')),
                        progress: value.status.includes(this.$t('audit.status.progress')),
                        scheduled: value.status.includes(this.$t('audit.status.scheduled'))
                    }

                default:
                    return ''
            }
        },
        preview(value, type) {
            // Método mantenido por compatibilidad, pero sin funcionalidad de imágenes
        },

        employee(id) {
            return this.$store.getters['employee/getEmployee'](id)
        },
        getStatusColor(status) {
            const statusColorMap = {
                completed: '#1ba844', // $color-success-500
                progress: '#dba600', // $color-warning-500
                scheduled: '#b3b3b3', // $color-neutral-500
                to_start: '#bd1f1e' // $color-error-500
            }
            return statusColorMap[status] || '#b3b3b3'
        },
        getStatusData(status) {
            if (!status || status === '-') {
                return null
            }
            return {
                color: this.getStatusColor(status),
                name: this.$t('audit.status.' + status)
            }
        },
        /**
         * Parsea y normaliza los IDs de auditoría del campo de búsqueda
         * Permite uno o varios IDs separados por comas, con o sin espacios
         * Ejemplos válidos: "02", "02,105", "02, 105", "02 ,105 , 206"
         * @param {string} searchText - Texto de búsqueda que puede contener IDs
         * @returns {Array<string>} - Array de IDs normalizados (sin espacios, sin ceros a la izquierda)
         */
        parseAuditIds(searchText) {
            if (!searchText || typeof searchText !== 'string') {
                return []
            }

            const trimmedText = searchText.trim()

            // Detectar si el texto contiene solo números y comas (posible lista de IDs)
            // Permitir espacios alrededor de las comas y al inicio/final
            // El patrón debe ser: números opcionales, seguidos de (coma + espacios + números) repetido
            const auditIdPattern = /^[\d\s,]+$/
            if (!auditIdPattern.test(trimmedText)) {
                return []
            }

            // Verificar que después de eliminar espacios, solo queden números y comas
            const withoutSpaces = trimmedText.replace(/\s/g, '')
            const onlyNumbersAndCommas = /^[\d,]+$/.test(withoutSpaces)

            if (!onlyNumbersAndCommas) {
                return []
            }

            // Dividir por comas y normalizar cada ID
            const ids = trimmedText
                .split(',')
                .map((id) => id.trim())
                .filter((id) => id.length > 0 && /^\d+$/.test(id.replace(/\s/g, '')))
                .map((id) => {
                    // Eliminar todos los espacios y ceros a la izquierda
                    const cleanId = id.replace(/\s/g, '').replace(/^0+/, '') || '0'
                    return cleanId
                })
                .filter((id) => id.length > 0)

            return ids
        }
    },
    watch: {},
    filters: {
        moment: function (date) {
            return date ? moment(date).fromNow() : '--'
        }
    },
    created() {
        const num_page = this.$store.getters['audit/getNumberPage']
        if (num_page) {
            this.page = num_page
            this.$store.commit('audit/setNumberPage', num_page)
        }
    },
    mounted() {
        if (this.$route.params.shouldShowActionPlanTable) {
            this.activeTab = 'action_plan'

            if (this.$route.params.pageNumber) {
                this.$store.commit('actionplan/setPageNumber', this.$route.params.pageNumber)
            }
        }
    }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
#content.audit {
    $column-width: 0%;
    $column-max-width: 0px;
    $content-width: 100%;

    >.content {
        padding-top: 0 !important;

        .detail {
            width: inherit;
            height: 100%;
            padding: 8px;

            .search {
                @include display-flex();
                @include align-items(center);
                @include background($image: img('search_neutro.svg'), $size: 25px, $position: left 12px center);
                @include border-radius(4px);
                @include font-size(ml);
                height: 40px;
                max-height: 40px;
                width: 300px;
                box-sizing: border-box;
                background-color: #fff;
                padding: 0 60px 0 45px;
                border-bottom: none;
                font-family: $text;
                cursor: text;
                margin: 0px;
            }

            .mobile-search {
                display: none;
            }

            .top-container {
                @include display-flex();
                justify-content: space-between;
                width: 100%;
                min-width: 100%;
                padding-bottom: 8px;

                .top-actions-container {
                    display: flex;
                    align-items: stretch;
                    background-color: $default-bg-color;
                    gap: 8px;

                    button {
                        margin: 0px !important;
                    }
                }

                .edit-columns {
                    @include display-flex();
                    @include align-items(center);
                    @include background($image: img('settings.svg'), $size: 20px, $position: center);
                    @include border-radius(4px);
                    @include font-size(ml);
                    height: 40px;
                    width: 40px;
                    min-width: 40px;
                    min-height: 40px;
                    background-color: #fff;
                    font-family: $text;
                    cursor: pointer;
                    margin-right: 8px;
                }

                .add-report {
                    @include display-flex();
                    @include align-items(center);
                    @include background($image: img('add_ffffff.svg'), $size: 20px, $position: center);
                    @include border-radius(4px);
                    @include font-size(ml);
                    height: 40px;
                    width: 40px;
                    min-width: 40px;
                    min-height: 40px;
                    background-color: #1a9980;
                    font-family: $text;
                    cursor: pointer;
                }
            }

            &.landscape,
            &.desktop {
                .top-container {
                    > div:first-of-type {
                        flex: 1 1 0;
                        min-width: 0;
                        overflow: hidden;
                    }

                    #info-container {
                        min-width: 0;

                        .detail-info .detail-title {
                            line-height: 130%;
                            
                            display: -webkit-box;
                            -webkit-line-clamp: 2;
                            -webkit-box-orient: vertical;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }
                    }
                }
            }

            .tabs-container {
                width: 100%;
                height: fit-content;
                display: grid;
                place-items: center;
                padding-bottom: 8px;

                .tabs {
                    height: 49px;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    align-items: center;
                    width: 100%;
                    background-color: white;
                    padding: 4px;
                    gap: 4px;
                    border-radius: 4px;

                    .tab {
                        @include display-flex();
                        width: 100%;
                        height: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 16px;
                        border-radius: 4px;
                        color: #727477;
                        gap: 6px;

                        background-color: $color-neutral-50;

                        .tab-text {
                            font-family: $text;

                            .open-count {
                                margin-left: 4px;
                            }
                        }

                        &.active {
                            background-color: $color-secondary-100;

                            .tab-text {
                                font-family: $text-bold;

                                .open-count {
                                    font-family: $text-bold;
                                }
                            }
                        }
                    }

                    .tab:hover {
                        background-color: white;
                        cursor: pointer;
                        background-color: $color-neutral-50;

                        &.active:hover {
                            background-color: $color-secondary-100;
                        }
                    }

                    .icon-container {
                        width: 16px;
                        height: 16px;
                        border-radius: 50%;
                        background-color: white;
                    }
                }
            }

            .table-container {
                width: 100%;
                height: calc(100% - 64px - 49px);
                overflow: auto;

                .table {
                    //height: 100%;
                    min-width: 800px;
                }
            }

            .pagination {
                width: 100%;
                padding: 6px 0px;
                padding-left: 12px;
                display: block;
                grid-column: 1 / 4;
                height: 45px;

                .item {
                    width: calc(50% - 10px);
                    height: 45px;
                    float: left;
                    @include border-radius(4px);
                    @extend .interaction;

                    &.next {
                        @include background($image: img('right_dark.svg'), $position: center center, $size: 20px, $color: $default-sec-color);
                        float: right;
                        margin-right: 8px;
                    }

                    &.prev {
                        @include background($image: img('left_dark.svg'), $position: center center, $size: 20px, $color: $default-sec-color);
                    }
                }
            }

            // .table {
            //     padding-left: 16px;
            //     height: calc(100% - 65px);
            // }

            &.desktop {
                .table-container {
                    width: 100%;
                    height: calc(100% - 120px);
                    overflow: hidden;
                }
            }

            &.landscape {
                .table-container {
                    width: 100%;
                    height: calc(100% - 74px);
                    overflow: auto;

                    &.hasTabs {
                        height: calc(100% - 74px - 49px);
                    }
                }
            }
        }
    }
}
</style>

<style lang="scss">
#content.audit {
    width: 100%;

    >.content {
        .audit-score {
            display: flex;
            align-items: center;
            gap: 7px;
        }

        .final-score-color {
            border-radius: 50%;
            min-width: 14px;
            width: 14px;
            height: 14px;
        }

        .table .table-values .column-values.draft {
            background-color: $color-white;
        }

        >.detail.portrait {
            width: 100%;
            height: 100%;
            overflow: hidden;

            .audit-info {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;

                .color-text-tag {
                    height: auto;
                    min-height: 25px;
                    padding-top: 2px;
                    padding-bottom: 2px;
                    box-sizing: border-box;

                    .tag-text {
                        line-height: 18px;
                    }
                }

                .final-score {
                    display: flex;
                    align-items: center;
                    gap: 0.5em;
                }

                .name-and-score {
                    font-size: 16px !important;
                    font-family: 'Avenir-Regular';
                    margin: 0 !important;
                    padding-top: 2px;
                    line-height: normal;
                }
            }

            .top-container {
                background-color: $default-bg-color;
                width: 100%;
                padding: 0px 0px 16px 0px;
                max-height: 150px;

                .search {
                    display: none;
                }
            }

            >.mobile-search {
                display: flex;
                width: 100%;
                flex-shrink: 0;
                margin-bottom: 8px;
            }

            .action-plans-table {
                height: calc(100% - 164px);
            }

            .table-container {
                height: calc(100% - 161px);
            }

            .table {
                // height: calc(100% - 120px);
                padding-left: 9px;
                overflow: auto;

                .table-values {
                    @include font-size(xs);

                    .column-values {
                        @include font-size(xs);

                        .audit-info {
                            display: flex;
                            flex-direction: column;
                            align-content: flex-start;
                            align-items: flex-start;
                            gap: 8px;

                            .final-score {
                                display: flex;
                                align-items: center;
                                gap: 0.5em;
                            }

                            .final-score-color {
                                border-radius: 50%;
                                min-width: 14px;
                                width: 14px;
                                height: 14px;
                            }

                            .name-and-score {
                                font-size: 16px !important;
                                font-family: 'Avenir-Regular';
                                margin: 0 !important;
                                padding-top: 2px;
                                line-height: normal;
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>
